import React, { useState } from 'react';
import { Slideout } from '../slideout';
import { Button } from 'react-bootstrap';
import { FormattedMessage } from 'react-intl';
import { Formik, Form, Field } from 'formik';
import * as yup from 'yup';
import { useMutation, useQuery } from '@apollo/client';
import { mutations, useTeamPermissions, graphqlErrorsToUiErrors } from '../../api';
import { useNotifications } from '../../notifications';
import { useTeamInfo } from '../../team-id-context';
import { useDealId } from '../../deal-id-context';
import { Permissions } from '../../shared/constants/permissions';
import { getTeamByIdWithDeals } from '../../api/queries';
import { EMAIL_VALIDATION_REGEX } from '../../shared/constants/emailValidation';
import ErrorMessage from '../error-message';
import LoadingSpinner from '../loading-spinner';
import EmailMultiSelect from '../shared/EmailMultiSelect';
import Toggle from '../toggle';
import SubmitButton from '../submit-button';
import { Warning } from '../shared/warning';
import {
  DUPLICATE_EMAIL_WARNING,
  ORG_MEMBER_EMAIL_WARNING,
  PENDING_EMAIL_WARNING,
} from '../document-wizard/document-editor/constants/EditorConstats';
import { ReactComponent as ChevronUpIcon } from '../../shared/icons/chevron-up.svg';
import { ReactComponent as ChevronDownIcon } from '../../shared/icons/chevron-down.svg';
import PermissionIcon from '../permission-icon';
import styles from './styles.module.scss';
import { TitleFormattedMessage } from '../titleFormattedMessage';

interface UserPermissions {
  email: string;
  permissionIds: Set<string>;
}

enum EMAIL_VALIDATION_ERRORS {
  existing,
  pending,
  orgMember,
  none,
}

const schema = yup.object().shape({
  teamId: yup.string().required(),
  additionalMessage: yup.string(),
});

interface InviteDealTeamSlideoutProps {
  isOpen: boolean;
  onClose: () => void;
}

const InviteDealTeamSlideout = ({ isOpen, onClose }: InviteDealTeamSlideoutProps) => {
  const [invitationEmails, setInvitationEmails] = useState<string[]>([]);
  const [userPermissions, setUserPermissions] = useState<UserPermissions[]>([]);
  const [expandedEmails, setExpandedEmails] = useState<Set<string>>(new Set());
  const [emailValidationError, setEmailValidationError] = useState<EMAIL_VALIDATION_ERRORS>(
    EMAIL_VALIDATION_ERRORS.none
  );
  const { loading: teamInfoLoading, teamId } = useTeamInfo();
  const { loading: permissionsLoading, teamPermissions } = useTeamPermissions();
  const [, { error: notifyError, success: notifySuccess }] = useNotifications();
  const [inviteNewTeamMember] = useMutation(mutations.inviteNewTeamMembersByEmail);
  const dealId = useDealId();

  const {
    loading: isTeamLoading,
    data: teamData,
    error: teamGraphqlError,
  } = useQuery(getTeamByIdWithDeals, {
    skip: !teamId,
    variables: { teamId },
  });

  const loading = teamInfoLoading || permissionsLoading || isTeamLoading;

  // Update email validation error when emails change
  React.useEffect(() => {
    if (!teamData || invitationEmails.length === 0) {
      setEmailValidationError(EMAIL_VALIDATION_ERRORS.none);
      return;
    }

    const lastEmail = invitationEmails[invitationEmails.length - 1];
    const orgMembers = teamData.teamById.teamMembers.map((member: any) => member.user.email);
    const dealMembers = teamData.teamById.teamMembers
      .filter((member: any) => {
        return member.user.deals.some((deal: any) => deal.id === dealId);
      })
      .map((member: any) => member.user.email);
    const pendingInviteEmails = teamData.teamById.pendingInvitations.map((user: { email: string }) => user.email);

    let error = EMAIL_VALIDATION_ERRORS.none;
    if (orgMembers.includes(lastEmail)) error = EMAIL_VALIDATION_ERRORS.orgMember;
    else if (dealMembers.includes(lastEmail)) error = EMAIL_VALIDATION_ERRORS.existing;
    else if (pendingInviteEmails.includes(lastEmail)) error = EMAIL_VALIDATION_ERRORS.pending;

    setEmailValidationError(error);
  }, [invitationEmails, dealId, teamData]);

  // Update user permissions when emails change
  React.useEffect(() => {
    const getPermissionIdByKey = (key: string) => teamPermissions?.find((p: any) => p.key === key)?.id;

    // Add new userPermissions when a new email is added
    const newEmails = invitationEmails.filter(
      (email) => !userPermissions.some((invite) => invite.email === email)
    );

    if (newEmails.length > 0) {
      newEmails.forEach((newEmail) => {
        const viewPermissionId = getPermissionIdByKey(Permissions.View);
        userPermissions.push({
          email: newEmail,
          permissionIds: new Set<string>(viewPermissionId ? [viewPermissionId] : []),
        });
      });

      // When a new email is added, close all cards and open only the newest one
      const latestEmail = newEmails[newEmails.length - 1];
      setExpandedEmails(new Set([latestEmail]));
    }

    // Remove userPermissions when an email is removed
    const filteredUserPermissionsList = userPermissions.filter((userPermission) =>
      invitationEmails.includes(userPermission.email)
    );

    setUserPermissions([...filteredUserPermissionsList]);
  }, [invitationEmails, teamPermissions]);

  if (loading) {
    return (
      <Slideout isOpen={isOpen} onClose={onClose} title="Invite People to Your Deal Team" width="medium">
        <LoadingSpinner />
      </Slideout>
    );
  }

  if (!teamData) {
    const errors = graphqlErrorsToUiErrors(teamGraphqlError);
    return (
      <Slideout isOpen={isOpen} onClose={onClose} title="Invite People to Your Deal Team" width="medium">
        <ErrorMessage>{errors._general}</ErrorMessage>
      </Slideout>
    );
  }

  const validateEmail = (email: string) => {
    const orgMembers = teamData.teamById.teamMembers.map((member: any) => member.user.email);
    const dealMembers = teamData.teamById.teamMembers
      .filter((member: any) => member.user.deals.some((deal: any) => deal.id === dealId))
      .map((member: any) => member.user.email);
    const pendingInviteEmails = teamData.teamById.pendingInvitations.map((user: { email: string }) => user.email);
    const validEmail = EMAIL_VALIDATION_REGEX.test(String(email).toLowerCase());

    return (
      !(orgMembers.includes(email) || pendingInviteEmails.includes(email) || dealMembers.includes(email)) && validEmail
    );
  };

  const getPermissionIdByKey = (key: string) => teamPermissions?.find((p: any) => p.key === key)?.id;

  const togglePermission = (email: string, permissionId: string) => {
    const commentPermissionId = getPermissionIdByKey(Permissions.Comment);
    const editPermissionId = getPermissionIdByKey(Permissions.Edit);
    const approvePermissionId = getPermissionIdByKey(Permissions.Approve);
    const editTemplatesPermissionId = getPermissionIdByKey(Permissions.EditTemplates);
    const manageTemplatesPermissionId = getPermissionIdByKey(Permissions.ManageTemplates);

    setUserPermissions((prev) =>
      prev.map((user) => {
        if (user.email !== email) return user;

        const newPermissionIds = new Set(user.permissionIds);

        // Handle cascading permissions
        if (permissionId === editPermissionId) {
          if (newPermissionIds.has(permissionId)) {
            newPermissionIds.delete(permissionId);
          } else {
            if (commentPermissionId) newPermissionIds.add(commentPermissionId);
            newPermissionIds.add(permissionId);
          }
        } else if (permissionId === approvePermissionId) {
          if (newPermissionIds.has(permissionId)) {
            newPermissionIds.delete(permissionId);
          } else {
            if (commentPermissionId) newPermissionIds.add(commentPermissionId);
            if (editPermissionId) newPermissionIds.add(editPermissionId);
            newPermissionIds.add(permissionId);
          }
        } else if (permissionId === manageTemplatesPermissionId) {
          if (newPermissionIds.has(permissionId)) {
            newPermissionIds.delete(permissionId);
          } else {
            if (editTemplatesPermissionId) newPermissionIds.add(editTemplatesPermissionId);
            if (editPermissionId) newPermissionIds.add(editPermissionId);
            if (commentPermissionId) newPermissionIds.add(commentPermissionId);
            if (approvePermissionId) newPermissionIds.add(approvePermissionId);
            newPermissionIds.add(permissionId);
          }
        } else {
          // Simple toggle for other permissions
          if (newPermissionIds.has(permissionId)) {
            newPermissionIds.delete(permissionId);
          } else {
            newPermissionIds.add(permissionId);
          }
        }

        return { ...user, permissionIds: newPermissionIds };
      })
    );
  };

  const toggleExpanded = (email: string) => {
    setExpandedEmails((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(email)) {
        newSet.delete(email);
      } else {
        newSet.add(email);
      }
      return newSet;
    });
  };

  const renderEmailErrorMsg = () => {
    switch (emailValidationError) {
      case EMAIL_VALIDATION_ERRORS.existing:
        return <Warning text={DUPLICATE_EMAIL_WARNING} />;
      case EMAIL_VALIDATION_ERRORS.pending:
        return <Warning text={PENDING_EMAIL_WARNING} />;
      case EMAIL_VALIDATION_ERRORS.orgMember:
        return <Warning text={ORG_MEMBER_EMAIL_WARNING} />;
      default:
        return null;
    }
  };

  const onSubmit = async (values: { [key: string]: any }) => {
    const apiUserPermissions = userPermissions.map((item) => ({
      email: item.email,
      permissionIds: Array.from(item.permissionIds),
    }));

    const data = {
      userPermissions: apiUserPermissions,
      emails: invitationEmails,
      defaultDeals: [dealId],
      teamId,
      additionalMessage: values.additionalMessage,
    };

    try {
      await inviteNewTeamMember({ variables: data });
      const inviteCount = invitationEmails.length;
      const message = inviteCount === 1
        ? 'Invitation sent successfully'
        : `${inviteCount} invitations sent successfully`;
      notifySuccess({ message });
      onClose();
    } catch (error) {
      notifyError({ message: error instanceof Error ? error.message : String(error) });
    }
  };

  const filteredPermissions = teamPermissions
    ?.filter((p: any) => p.key !== Permissions.Whitelist)
    .sort((a: any, b: any) => a.displayOrder - b.displayOrder);

  return (
    <Slideout isOpen={isOpen} onClose={onClose} title="Invite People to Your Deal Team" width="medium">
      <Formik
        validationSchema={schema}
        onSubmit={onSubmit}
        initialValues={{
          teamId,
          additionalMessage: '',
        }}
      >
        {({ errors, isSubmitting }) => (
          <Form className={styles.slideoutForm}>
            <div className={styles.slideoutContent}>
              <div className={styles.emailSection}>
                <h3>
                  <FormattedMessage id="title.email-address" defaultMessage="Email address" />
                </h3>
                <span className="text-muted">
                  <FormattedMessage
                    id="phrase.new-team-member-will-receive-activation-email"
                    defaultMessage="Your new team member will receive an email to activate their account."
                  />
                </span>
                <div className="mt-2">
                  <EmailMultiSelect
                    emails={invitationEmails}
                    onChange={setInvitationEmails}
                    validateEmail={validateEmail}
                    placeholder="Enter email addresses..."
                    getLabel={(email, index, removeEmail) => (
                      <div data-tag key={index}>
                        {email}
                        <span data-tag-handle onClick={() => removeEmail(index)}>
                          ×
                        </span>
                      </div>
                    )}
                  />
                  {renderEmailErrorMsg()}
                </div>
              </div>

              <div className={styles.cardsContainer}>
                {userPermissions.map((user) => {
                  const isExpanded = expandedEmails.has(user.email);
                  const editPermissionId = getPermissionIdByKey(Permissions.Edit);
                  const approvePermissionId = getPermissionIdByKey(Permissions.Approve);
                  const manageTemplatesPermissionId = getPermissionIdByKey(Permissions.ManageTemplates);

                  return (
                    <div key={user.email} className={styles.permissionCard}>
                      <button
                        type="button"
                        className={styles.cardHeader}
                        onClick={() => toggleExpanded(user.email)}
                      >
                        <span className={styles.cardEmail}>{user.email}</span>
                        <span className={styles.cardToggle}>
                          {isExpanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
                        </span>
                      </button>
                      {isExpanded && (
                        <div className={styles.cardBody}>
                          <h4 className={styles.permissionsTitle}>
                            <FormattedMessage id="title.permissions" defaultMessage="Permissions" />
                          </h4>
                          {filteredPermissions?.map((permission: any) => {
                            const isView = permission.key === Permissions.View;
                            const isComment = permission.key === Permissions.Comment;
                            const isEdit = permission.key === Permissions.Edit;
                            const isEditTemplates = permission.key === Permissions.EditTemplates;
                            const isApprove = permission.key === Permissions.Approve;

                            const isCommentDisabled = isComment && editPermissionId && user.permissionIds.has(editPermissionId);
                            const isEditDisabled = isEdit && approvePermissionId && user.permissionIds.has(approvePermissionId);
                            const isApproveDisabled = isApprove && manageTemplatesPermissionId && user.permissionIds.has(manageTemplatesPermissionId);
                            const isEditTemplatesDisabled = isEditTemplates && manageTemplatesPermissionId && user.permissionIds.has(manageTemplatesPermissionId);

                            return (
                              <div key={permission.id} className={styles.permissionRow}>
                                <div className={styles.permissionLabel}>
                                  <PermissionIcon permission={permission} withAltText={false} />
                                  <span>{permission.name}</span>
                                </div>
                                <Toggle
                                  checked={user.permissionIds.has(permission.id)}
                                  disabled={isView || isCommentDisabled || isEditDisabled || isApproveDisabled || isEditTemplatesDisabled}
                                  isCustomDisabledState={isView}
                                  onChange={() => togglePermission(user.email, permission.id)}
                                />
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            <div className={styles.stickyFooter}>
              <div className={styles.messageSection}>
                <h3>
                  <FormattedMessage id="title.additional-message" defaultMessage="Additional Message" />
                </h3>
                <Field
                  className="form-control"
                  name="additionalMessage"
                  component="textarea"
                  placeholder="Message (Optional)"
                  rows={3}
                />
              </div>

              <div className={styles.actions}>
                <Button variant="link" className="text-secondary" onClick={onClose}>
                  <TitleFormattedMessage id="cta.cancel" defaultMessage="Cancel" />
                </Button>
                <SubmitButton
                  label={<FormattedMessage id="cta.send-invitation-email" defaultMessage="Send invitation email" />}
                  disabled={isSubmitting || invitationEmails.length === 0}
                  isSubmitting={isSubmitting}
                />
              </div>
            </div>
          </Form>
        )}
      </Formik>
    </Slideout>
  );
};

export default InviteDealTeamSlideout;
